/**
 * ObjectCodeBadge - Badge que muestra el código único de un objeto del mapa
 *
 * @version 1.0
 * @updated 2026-02-18
 * @module core/coding
 *
 * @description
 * Muestra el código de codificación de un objeto (e.g. "TR_AA001_005") en un
 * badge compacto con fuente monoespaciada. Tiene variantes para:
 * - Código existente: muestra el código con fondo azul semitransparente
 * - Pendiente (objeto nuevo): texto en cursiva indicando que se asignará al guardar
 * - Verificando: animación de pulso mientras se consulta la API
 */

import './coding.css';

/**
 * Props del componente
 */
export interface ObjectCodeBadgeProps {
  /**
   * Código del objeto (e.g. "TR_AA001_005").
   * Si es null/undefined/vacío, muestra estado pendiente.
   */
  code?: string | null;
  /**
   * true cuando se está verificando un cambio de código (muestra pulso)
   */
  isChecking?: boolean;
  /**
   * true si el objeto es nuevo y el código se generará al guardar
   */
  isPending?: boolean;
}

/**
 * Badge que muestra el código de un objeto del mapa
 *
 * @example
 * ```tsx
 * // Objeto existente
 * <ObjectCodeBadge code="TR_AA001_005" />
 *
 * // Objeto nuevo - código se generará al guardar
 * <ObjectCodeBadge isPending />
 *
 * // Verificando si cambió de celda
 * <ObjectCodeBadge code="TR_AA001_005" isChecking />
 * ```
 */
export function ObjectCodeBadge({
  code,
  isChecking = false,
  isPending = false,
}: ObjectCodeBadgeProps): JSX.Element {
  if (isChecking) {
    return (
      <span className="object-code-badge object-code-badge--checking" title="Verificando celda...">
        {code || '···'}
      </span>
    );
  }

  if (isPending || !code) {
    return (
      <span
        className="object-code-badge object-code-badge--pending"
        title="El código se asignará al guardar"
      >
        Se asignará al guardar
      </span>
    );
  }

  return (
    <span className="object-code-badge" title={`Código del objeto: ${code}`}>
      {code}
    </span>
  );
}
